import { ref, computed, watch, Ref, readonly } from "vue";
import { db, realtimeDb } from "@/fire";
import {
  deleteField,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref as firebaseRef, set as firebaseSet } from "firebase/database";
import { createGlobalState, useSessionStorage, useThrottleFn } from "@vueuse/core";
import { useFirestore } from "@vueuse/firebase";
import { v4 as uuid4 } from "uuid";
import { LaserPointer, Presentation, Rect } from "@/models";
import { useStore } from "vuex";
import type { DownloadableDocumentFragment } from "@/graphql/operations";
import {
  FULL_PAGE_ZOOM,
  LASER_HIDDEN,
} from "@/components/PdfJsPresenter.zoom";

type ProjectorDocument = Pick<
  DownloadableDocumentFragment,
  "deletedAt" | "downloadUrl" | "firestoreId" | "mimeType" | "pages"
>;

export type ProjectorVideoState = {
  state: string;
  position: number;
  documentIdentity: string | null;
};

export function getPresentableDocument(
  value: ProjectorDocument | undefined,
): ProjectorDocument | undefined {
  return value?.deletedAt === null ? value : undefined;
}

export function getProjectorDocumentIdentity(
  value: ProjectorDocument | undefined,
): string | null {
  const currentDocument = getPresentableDocument(value);
  if (!currentDocument) return null;

  return [
    currentDocument.firestoreId,
    currentDocument.downloadUrl,
    currentDocument.mimeType,
    currentDocument.pages,
  ].join("\u0000");
}

export const useProjector = createGlobalState(() => {
  const store = useStore();
  const generateCode = () => Math.floor(1000 + Math.random() * 9000).toString();

  const isEnabled = useSessionStorage("projector-enabled", false);
  const presentationId = useSessionStorage<string | null>(
    "presentation-id",
    null
  );
  const code = useSessionStorage<string | null>("presentation-code", null);
  const document = ref<ProjectorDocument>();
  const page = ref(1);
  const videoState = ref<ProjectorVideoState | null>(null);
  const zoom = ref<Rect>({ ...FULL_PAGE_ZOOM });
  const laserpointer = ref<LaserPointer>({ ...LASER_HIDDEN });

  const presentation = useFirestore(
    computed(
      () =>
        !!presentationId.value && doc(db, "presentations", presentationId.value)
    ),
    undefined
  ) as Ref<Presentation | null | undefined>;

  const brandURL = computed(
    () =>
      store.getters.user.mergedConfig?.brandUrl ?? ""
  );
  const shareLink = computed(
    () => store.getters.user.mergedConfig?.shareLink ?? "projektor.live"
  );

  watch(isEnabled, (nowIsEnabled) => {
    presentationId.value = nowIsEnabled ? uuid4() : null;
    code.value = nowIsEnabled ? generateCode() : null;
  });

  watch(
    presentationId,
    (newPresentationId, oldPresentationId) => {
      if (newPresentationId && !oldPresentationId) {
        const currentDocument = getPresentableDocument(document.value);
        if (!currentDocument) {
          videoState.value = null;
        }

        const documentData: any = {
          pages: currentDocument?.pages ?? null,
          page: page.value,
          uploadedAt: serverTimestamp(),
          name: "__placeholder_name__",
          code: code.value ?? "invalid",
          isViewOnly: true,
          isPaused: !currentDocument?.downloadUrl,
          lastPageUpdateId: uuid4(),
          url: currentDocument?.downloadUrl ?? "placeholder",
          brandURL: brandURL.value,
          mimeType: currentDocument?.mimeType ?? "",
          documentId: currentDocument?.firestoreId ?? "placeholder",
        };

        if (
          currentDocument &&
          currentDocument.mimeType !== "application/pdf" &&
          videoState.value?.documentIdentity ===
            getProjectorDocumentIdentity(currentDocument)
        ) {
          documentData.page = -1;
          documentData.video = {
            state: videoState.value.state,
            stateChangedAt: Date.now(),
            stateChangedAtPosition: videoState.value.position,
          };
        }

        setDoc(doc(db, "presentations", newPresentationId), documentData);
      }

      if (oldPresentationId && !newPresentationId) {
        updateDoc(doc(db, "presentations", oldPresentationId), {
          isPaused: true,
          url: "placeholder",
          code: "invalid",
          lastPageUpdateId: uuid4(),
          documentId: "placeholder",
          pages: null,
          mimeType: "",
          video: deleteField(),
        });
      }
    },
    { immediate: true },
  );

  watch(
    [document, page],
    ([rawCurrentDocument, currentPage], [rawPreviousDocument]) => {
      const currentDocument = getPresentableDocument(rawCurrentDocument);
      const senderChanged =
        getProjectorDocumentIdentity(rawCurrentDocument) !==
        getProjectorDocumentIdentity(rawPreviousDocument);

      if (
        !currentDocument ||
        currentDocument.mimeType === "application/pdf" ||
        senderChanged
      ) {
        videoState.value = null;
      }

      const id = presentationId.value;
      if (!id) return;

      if (currentDocument) {
        const documentData: any = {
          url: currentDocument.downloadUrl,
          isPaused: false,
          page:
            currentDocument.mimeType === "application/pdf" ? currentPage : -1,
          lastPageUpdateId: uuid4(),
          mimeType: currentDocument.mimeType ?? "",
          documentId: currentDocument.firestoreId,
          pages: currentDocument.pages,
        };

        if (
          senderChanged ||
          currentDocument.mimeType === "application/pdf"
        ) {
          documentData.video = deleteField();
        }

        updateDoc(doc(db, "presentations", id), documentData);
        return;
      }

      updateDoc(doc(db, "presentations", id), {
        url: "placeholder",
        isPaused: true,
        page: currentPage ?? null, // currentPage might be undefined, and undefined is an unsupported type in firestore
        lastPageUpdateId: uuid4(),
        documentId: "placeholder",
        pages: null,
        mimeType: "",
        video: deleteField(),
      });
    },
  );

  // Use stable projected fields rather than the GraphQL object identity so a
  // cache refresh of the same document cannot reset interaction state without
  // also changing the presenter's logical sender context.
  const documentIdentity = computed(() =>
    getProjectorDocumentIdentity(document.value),
  );

  // Zoom: reset to full page whenever the projected slide changes — a new
  // page, a different document payload, or a new presentation — then stream
  // changes to Realtime DB while the projector is active.
  watch([page, documentIdentity, presentationId], () => {
    zoom.value = { ...FULL_PAGE_ZOOM };
  });

  const throttledWriteZoom = useThrottleFn(
    (rect: Rect, id: string) => {
      firebaseSet(firebaseRef(realtimeDb, `/presentations/${id}/zoom`), rect);
    },
    80,
    true,
  );

  watch(zoom, (newZoom) => {
    const id = presentationId.value;
    if (!isEnabled.value || !id) return;
    throttledWriteZoom({ ...newZoom }, id);
  }, { deep: true });

  // Laserpointer: hide whenever the projected slide changes (page, document,
  // or presentation). The wire point is in unrotated PDF logical coordinates,
  // so the old position would land at an unrelated location on the new slide.
  watch([page, documentIdentity, presentationId], () => {
    laserpointer.value = { ...LASER_HIDDEN };
  });

  const throttledWriteLaserpointer = useThrottleFn(
    (p: LaserPointer, id: string) => {
      firebaseSet(
        firebaseRef(realtimeDb, `/presentations/${id}/laserpointer`),
        p,
      );
    },
    50,
    true,
  );

  watch(
    laserpointer,
    (newPoint) => {
      const id = presentationId.value;
      if (!isEnabled.value || !id) return;
      throttledWriteLaserpointer({ ...newPoint }, id);
    },
    { deep: true },
  );

  watch(videoState, (newVideoState, oldVideoState) => {
    const id = presentationId.value;
    const currentDocument = getPresentableDocument(document.value);
    if (
      !id ||
      !currentDocument ||
      currentDocument.mimeType === "application/pdf" ||
      newVideoState?.documentIdentity !==
        getProjectorDocumentIdentity(currentDocument)
    ) {
      return;
    }

    if (newVideoState) {
      updateDoc(doc(db, "presentations", id), {
        lastPageUpdateId: uuid4(),
        video: {
          state: newVideoState.state,
          stateChangedAt: serverTimestamp(),
          stateChangedAtPosition: newVideoState.position
        }
      });
    }
  });

  return {
    isEnabled,
    document,
    page,
    zoom,
    laserpointer,
    presentation,
    code: readonly(code),
    presentationId: readonly(presentationId),
    brandURL,
    shareLink,
    videoState
  };
});
